/**
 * Base UI primitives.
 *
 * Small, unopinionated, and used everywhere. Anything domain-specific — a trust
 * score, a provenance chip — lives in `components/data/` instead, so this file
 * stays reusable and the domain components stay honest about what they mean.
 */

import { forwardRef } from 'react'
import type {
  AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes,
  InputHTMLAttributes, ReactNode, SelectHTMLAttributes,
} from 'react'
import { Link } from 'react-router-dom'

/**
 * Join class names, dropping anything falsy.
 *
 * The parameter type includes `number` and `bigint` deliberately: `count &&
 * 'cls'` evaluates to `0` rather than `false`, and a narrower type would push
 * every caller into writing `count > 0 && …`. Filtering on truthiness handles
 * all of them.
 */
export function cx(
  ...classes: Array<string | number | bigint | false | null | undefined>
): string {
  return classes.filter((value) => typeof value === 'string' && value).join(' ')
}

/* -- Button ---------------------------------------------------------------- */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
type ButtonSize = 'sm' | 'md' | 'lg'

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded-md ' +
  'transition-colors duration-150 whitespace-nowrap ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] ' +
    'shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
  secondary:
    'bg-[var(--surface-panel)] text-[var(--text-primary)] border ' +
    'border-[var(--border-default)] hover:bg-[var(--surface-hover)]',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
  subtle:
    'bg-[var(--accent-subtle)] text-[var(--accent-text)] border ' +
    'border-[var(--accent-border)] hover:brightness-95',
  danger: 'bg-[var(--color-negative)] text-white hover:brightness-110',
}

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-6 text-base',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // A loading button must be non-interactive, or an impatient user submits
      // the same form three times.
      disabled={disabled || loading}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...rest}
    >
      {loading ? <Spinner className="size-3.5" /> : icon}
      {children}
    </button>
  )
})

interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
}

export function LinkButton({
  to, variant = 'primary', size = 'md', icon, className, children, ...rest
}: LinkButtonProps) {
  return (
    <Link
      to={to}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...rest}
    >
      {icon}
      {children}
    </Link>
  )
}

/* -- Spinner --------------------------------------------------------------- */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className ?? 'size-4')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

/* -- Badge ----------------------------------------------------------------- */
type BadgeTone =
  | 'neutral' | 'accent' | 'positive' | 'caution' | 'negative' | 'info'
  | 'verified' | 'official' | 'fact' | 'inference' | 'estimate' | 'stale'

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral:
    'bg-[var(--surface-sunken)] text-[var(--text-secondary)] border-[var(--border-subtle)]',
  accent:
    'bg-[var(--accent-subtle)] text-[var(--accent-text)] border-[var(--accent-border)]',
  positive: 'bg-[color-mix(in_oklch,var(--color-positive)_12%,transparent)] text-[var(--color-positive)] border-[color-mix(in_oklch,var(--color-positive)_28%,transparent)]',
  caution: 'bg-[color-mix(in_oklch,var(--color-caution)_14%,transparent)] text-[var(--color-caution)] border-[color-mix(in_oklch,var(--color-caution)_30%,transparent)]',
  negative: 'bg-[color-mix(in_oklch,var(--color-negative)_12%,transparent)] text-[var(--color-negative)] border-[color-mix(in_oklch,var(--color-negative)_28%,transparent)]',
  info: 'bg-[color-mix(in_oklch,var(--color-info)_12%,transparent)] text-[var(--color-info)] border-[color-mix(in_oklch,var(--color-info)_28%,transparent)]',
  verified: 'bg-[color-mix(in_oklch,var(--color-verified)_12%,transparent)] text-[var(--color-verified)] border-[color-mix(in_oklch,var(--color-verified)_28%,transparent)]',
  official: 'bg-[color-mix(in_oklch,var(--color-official)_12%,transparent)] text-[var(--color-official)] border-[color-mix(in_oklch,var(--color-official)_28%,transparent)]',
  fact: 'bg-[color-mix(in_oklch,var(--color-fact)_12%,transparent)] text-[var(--color-fact)] border-[color-mix(in_oklch,var(--color-fact)_28%,transparent)]',
  inference: 'bg-[color-mix(in_oklch,var(--color-inference)_14%,transparent)] text-[var(--color-inference)] border-[color-mix(in_oklch,var(--color-inference)_30%,transparent)]',
  estimate: 'bg-[color-mix(in_oklch,var(--color-estimate)_12%,transparent)] text-[var(--color-estimate)] border-[color-mix(in_oklch,var(--color-estimate)_28%,transparent)]',
  stale: 'bg-[color-mix(in_oklch,var(--color-stale)_12%,transparent)] text-[var(--color-stale)] border-[color-mix(in_oklch,var(--color-stale)_28%,transparent)]',
}

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
  icon?: ReactNode
  size?: 'xs' | 'sm'
}

export function Badge({
  tone = 'neutral', icon, size = 'sm', className, children, ...rest
}: BadgeProps) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap',
        size === 'xs' ? 'px-1.5 py-0 text-2xs' : 'px-2 py-0.5 text-xs',
        BADGE_TONES[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  )
}

/* -- Panel ----------------------------------------------------------------- */
interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  raised?: boolean
  padded?: boolean
}

export function Panel({ raised, padded = true, className, children, ...rest }: PanelProps) {
  return (
    <div
      className={cx(raised ? 'panel-raised' : 'panel', padded && 'p-5', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

interface SectionHeaderProps {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function SectionHeader({ title, description, action, className }: SectionHeaderProps) {
  return (
    <div className={cx('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/* -- Form controls --------------------------------------------------------- */
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  leading?: ReactNode
  trailing?: ReactNode
  /** See `SelectProps.fullWidth` — same class-order trap. */
  fullWidth?: boolean
  /**
   * Pill-shaped, sunken: the shape every search field in this product wears.
   *
   * It exists as a prop rather than a class each caller remembers, because the
   * app and the server-rendered catalogue share a header and a visitor crosses
   * between them constantly. One of the two drifting is how you end up looking
   * like two products.
   */
  search?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, leading, trailing, fullWidth = true, search, className, ...rest },
  ref,
) {
  return (
    <div className={cx('relative', fullWidth && 'w-full')}>
      {leading ? (
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--text-tertiary)]">
          {leading}
        </span>
      ) : null}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx(
          fullWidth && 'w-full',
          'h-9 border px-3 text-sm',
          search
            ? 'rounded-full bg-[var(--surface-sunken)]'
            : 'rounded-md bg-[var(--surface-panel)]',
          'text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]',
          'transition-colors outline-none',
          'focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20',
          invalid
            ? 'border-[var(--color-negative)]'
            : 'border-[var(--border-default)]',
          leading && 'pl-9',
          trailing && 'pr-9',
          className,
        )}
        {...rest}
      />
      {trailing ? (
        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--text-tertiary)]">
          {trailing}
        </span>
      ) : null}
    </div>
  )
})

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
  /**
   * Whether the control fills its container.
   *
   * An explicit prop, not a `w-auto` class in `className`. Tailwind resolves
   * `w-full` and `w-auto` by stylesheet order rather than by which one the
   * caller passed last, so overriding a base width from the outside silently
   * fails about half the time — which is how a toolbar dropdown ends up
   * spanning the whole page.
   */
  fullWidth?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, fullWidth = true, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(
        fullWidth && 'w-full',
        'h-9 rounded-md border bg-[var(--surface-panel)] px-3 pr-8 text-sm',
        'text-[var(--text-primary)] transition-colors outline-none appearance-none',
        'focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20',
        // The chevron is a background image so the control keeps native
        // keyboard behaviour — a custom listbox would have to rebuild it.
        "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22none%22 stroke=%22%23888%22 stroke-width=%221.6%22%3E%3Cpath d=%22M6 8l4 4 4-4%22/%3E%3C/svg%3E')] bg-[length:18px] bg-[right_0.5rem_center] bg-no-repeat",
        invalid ? 'border-[var(--color-negative)]' : 'border-[var(--border-default)]',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  )
})

interface FieldProps {
  label: string
  htmlFor?: string
  hint?: ReactNode
  errors?: string[]
  required?: boolean
  children: ReactNode
}

export function Field({ label, htmlFor, hint, errors, required, children }: FieldProps) {
  const hasErrors = Boolean(errors?.length)
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-xs font-medium text-[var(--text-secondary)]"
      >
        {label}
        {required ? <span className="ml-0.5 text-[var(--color-negative)]">*</span> : null}
      </label>
      {children}
      {/* Every error is shown, not just the first — the user should be able to
          fix the whole field in one pass. */}
      {hasErrors ? (
        <ul className="space-y-0.5">
          {errors!.map((message) => (
            <li key={message} className="text-xs text-[var(--color-negative)]">
              {message}
            </li>
          ))}
        </ul>
      ) : hint ? (
        <p className="text-xs text-[var(--text-tertiary)]">{hint}</p>
      ) : null}
    </div>
  )
}

/* -- Feedback -------------------------------------------------------------- */
interface AlertProps {
  tone?: 'info' | 'caution' | 'negative' | 'positive'
  title?: ReactNode
  children?: ReactNode
  className?: string
}

const ALERT_TONES = {
  info: 'border-[color-mix(in_oklch,var(--color-info)_30%,transparent)] bg-[color-mix(in_oklch,var(--color-info)_8%,transparent)] text-[var(--color-info)]',
  caution: 'border-[color-mix(in_oklch,var(--color-caution)_32%,transparent)] bg-[color-mix(in_oklch,var(--color-caution)_10%,transparent)] text-[var(--color-caution)]',
  negative: 'border-[color-mix(in_oklch,var(--color-negative)_30%,transparent)] bg-[color-mix(in_oklch,var(--color-negative)_8%,transparent)] text-[var(--color-negative)]',
  positive: 'border-[color-mix(in_oklch,var(--color-positive)_30%,transparent)] bg-[color-mix(in_oklch,var(--color-positive)_8%,transparent)] text-[var(--color-positive)]',
} as const

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  return (
    <div
      role="alert"
      className={cx('rounded-lg border px-4 py-3 text-sm', ALERT_TONES[tone], className)}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? (
        <div className={cx('text-[var(--text-secondary)]', title && 'mt-1')}>{children}</div>
      ) : null}
    </div>
  )
}

interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon ? (
        <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--text-tertiary)]">
          {icon}
        </div>
      ) : null}
      <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
      {description ? (
        <div className="mt-1 max-w-sm text-sm text-[var(--text-secondary)]">{description}</div>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

/** A loading placeholder shaped like the content it replaces. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('shimmer rounded-md', className ?? 'h-4 w-full')} />
}

/* -- Layout ---------------------------------------------------------------- */
export function Divider({ className }: { className?: string }) {
  return <hr className={cx('border-t border-[var(--border-subtle)]', className)} />
}

interface StatProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: 'default' | 'accent' | 'positive' | 'negative'
  className?: string
}

export function Stat({ label, value, hint, tone = 'default', className }: StatProps) {
  const toneClass = {
    default: 'text-[var(--text-primary)]',
    accent: 'text-[var(--accent-text)]',
    positive: 'text-[var(--color-positive)]',
    negative: 'text-[var(--color-negative)]',
  }[tone]

  return (
    <div className={className}>
      <p className="field-label">{label}</p>
      <p className={cx('mt-1 text-xl font-semibold tabular', toneClass)}>{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{hint}</p> : null}
    </div>
  )
}

/** A definition row — the workhorse of every detail panel. */
export function DataRow({
  label, value, className,
}: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-baseline justify-between gap-4 py-2', className)}>
      <dt className="shrink-0 text-xs text-[var(--text-tertiary)]">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-[var(--text-primary)] tabular">{value}</dd>
    </div>
  )
}
